//
// Copyright (c) 2025 Comcast
//

import { createSelector } from 'reselect'

import { shortAbbr } from 'utils/time'

// if you just need some user props use specific selectors below to prevent
// unnecessary component updates
export const user = (state) => {
  return state.sessionUser
}

const createSimpleSelector = (prop, defaultValue) => {
  return (state) => {
    const result = user(state)[prop]
    if (typeof defaultValue !== 'undefined') {
      return result || defaultValue
    } else {
      return result
    }
  }
}

export const id = createSimpleSelector('id')
export const email = createSimpleSelector('email')
export const isValidEmail = createSimpleSelector('validEmail')
export const login = createSimpleSelector('login')
export const role = createSimpleSelector('role')
export const capability = createSimpleSelector('capability')
export const firstName = createSimpleSelector('firstName', '')
export const lastName = createSimpleSelector('lastName', '')
export const fullName = createSimpleSelector('fullName', '')
export const avatarUrl = createSimpleSelector('avatarUrl')
export const updatedAt = createSimpleSelector('updatedAt')
export const timezone = createSimpleSelector('timezone')
export const autosubscriptionEnabled = createSimpleSelector('autosubscriptionEnabled')
export const isAdmin = createSimpleSelector('admin', false)
export const isAdminReadOnly = createSimpleSelector('adminReadOnly', false)
export const isPowerUser = createSimpleSelector('powerUser', false)
export const isPartnerManager = createSimpleSelector('partnerManager', false)
export const partnerId = createSimpleSelector('partnerId')
export const canAccessApiDoc = createSimpleSelector('canAccessApiDoc', false)
// Used for set correct partner on login page
export const mainUserPartnerId = createSimpleSelector('mainUserPartnerId')
export const partnerName = createSimpleSelector('partner')
export const partnerIcon = createSimpleSelector('partnerIcon')
export const homePage = createSimpleSelector('homePage')
export const displayName = createSimpleSelector('displayName')
export const locale = createSimpleSelector('locale')
export const timeFormat24 = createSimpleSelector('timeFormat24')
export const timezoneAutodetect = createSimpleSelector('timezoneAutodetect')
export const logoutEndpoint = createSimpleSelector('logoutEndpoint')
export const subscribedToTicketingSystems = createSimpleSelector('subscribedToTicketingSystems', [])
// The impersonation mode means user acts as other partner and
// views all data as other partner. Available for Comcast user roles.
export const landingPageTitle = createSimpleSelector('landingPageTitle', '')
export const landingPageText = createSimpleSelector('landingPageText', '')

// It's just list of enable features
// We can check it like this: features.includes(featureName)
export const features = createSimpleSelector('features')
export const tenantTicketIdFieldName = createSimpleSelector('tenantTicketIdFieldName')
export const otelEnabled = createSimpleSelector('otelEnabled', false)
export const megaMenu = createSimpleSelector('megaMenu')
export const ticketProviders = createSimpleSelector('ticketProviderList')
export const partnerTicketingSystems = createSimpleSelector('partnerTicketingSystemList')
export const notificationReleaseNote = createSimpleSelector('notificationReleaseNote')
export const newReleasesSubscriptionEnabled = createSimpleSelector('newReleasesSubscriptionEnabled')
export const newLogic = createSimpleSelector('newLogic')

export const isAdminNotReadOnly = createSelector(
  isAdmin, isAdminReadOnly,
  (isAdmin, isAdminReadOnly) => isAdmin && !isAdminReadOnly
)

export const isAnyAdmin = createSelector(
  isAdmin, isAdminReadOnly,
  (isAdmin, isAdminReadOnly) => (isAdmin || isAdminReadOnly)
)

export const isUserPrivileged = createSelector(
  isAdmin, isAdminReadOnly, isPowerUser,
  (isAdmin, isAdminReadOnly, isPowerUser) => (isAdmin || isAdminReadOnly || isPowerUser)
)

export const isAdminImpersonatedRole = createSelector(
  role,
  (role) => (role === 'admin_impersonated')
)

export const isPowerUserRole = createSelector(
  role,
  (role) => (role === 'power_user')
)

export const isPowerUserImpersonatedRole = createSelector(
  role,
  (role) => (role === 'power_user_impersonated')
)

export const timezoneAbbr = createSelector(
  timezone,
  (timezone) => shortAbbr(timezone)
)

export const partnerTicketingSystemsById = createSelector(
  partnerTicketingSystems,
  (partnerTicketingSystems) => {
    const ticketingSystemsById = {}
    partnerTicketingSystems?.forEach(partner => {
      partner.ticketingSystems.forEach(ts => {
        ticketingSystemsById[ts.id] = { ...ts, ...{ partner: partner } }
      })
    })
    return ticketingSystemsById
  }
)
